import React, { useState } from 'react';
import DashboardLayout from '../common/DashboardLayout';
import SupplyStorefront from '../common/SupplyStorefront';
import './devTools.css';
import { getApiBaseUrl } from '../../services/httpServices';

const DevTools = () => {
  const [environment, setEnvironment] = useState('local');
  const [email, setEmail] = useState('vortek1@aol.com');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // The API host comes from httpServices, which resolves it from the hostname and
  // rejects any loopback address. It was previously a hardcoded map whose staging
  // and production entries pointed at the angelwatch dot com properties - same
  // company, but separately managed infrastructure and not this platform's API -
  // so picking "Production" sent sysadmin credentials to the wrong system and no
  // token could ever come back. There is exactly one correct API for wherever this
  // admin app is served, so there is nothing to choose.
  const apiUrl = getApiBaseUrl().replace(/\/api\/?$/, '');

  const generateToken = async () => {
    setLoading(true);
    setError('');
    setToken('');

    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.token) {
        throw new Error('No token in response');
      }

      setToken(data.token);
    } catch (err) {
      setError(`Failed to generate token: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openSwagger = () => {
    const swaggerUrl = `${apiUrl}/api-docs`;
    window.open(swaggerUrl, '_blank');
  };

  return (
    <DashboardLayout role="admin" layoutType="topnav" activeMenu="Test" showBreadcrumbs={false}>
    <div className="dev-tools-page">
      <div className="dev-tools-container">
        <div className="page-header">
          <h1>🛠️ Developer Tools</h1>
          <p className="page-description">
            Generate JWT tokens and access API documentation for testing
          </p>
        </div>

        {/* Recommended Supply Store — sysadmin-only mock. This same box will sit at the
            top of every school admin dashboard once the Amazon affiliate link is set. */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
            🔒 Sysadmin preview — this box will appear at the top of every school admin dashboard once the Amazon affiliate storefront link is live.
          </div>
          <SupplyStorefront />
        </div>

        <div className="dev-tools-grid">
          {/* JWT Token Generator */}
          <div className="dev-tool-card">
            <h2>🔐 JWT Token Generator</h2>

            <div className="form-group">
              <label>API</label>
              <input className="form-control" value={apiUrl} readOnly />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="form-control"
              />
            </div>

            <button
              onClick={generateToken}
              disabled={loading || !email || !password}
              className="btn btn-primary btn-block"
            >
              {loading ? '⏳ Generating...' : '🔑 Generate Token'}
            </button>

            {error && (
              <div className="alert alert-danger mt-3">
                {error}
              </div>
            )}

            {token && (
              <div className="token-display mt-3">
                <div className="token-header">
                  <h3>✅ Token Generated</h3>
                  <button
                    onClick={copyToClipboard}
                    className="btn btn-sm btn-secondary"
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <div className="token-box">
                  <code>{token}</code>
                </div>
                <div className="token-info">
                  <p><strong>For Swagger:</strong> Paste this token in the Authorize dialog</p>
                  <p><strong>Format:</strong> Bearer {token.substring(0, 20)}...</p>
                </div>
              </div>
            )}
          </div>

          {/* Swagger UI Access */}
          <div className="dev-tool-card">
            <h2>📚 API Documentation</h2>

            <div className="swagger-info">
              <p>Access Swagger UI to test all API endpoints</p>
              <p className="api-url">
                <strong>API URL:</strong> {apiUrl}
              </p>
            </div>

            <button
              onClick={openSwagger}
              className="btn btn-primary btn-block btn-lg"
            >
              🚀 Open Swagger UI
            </button>

            <div className="swagger-instructions mt-3">
              <h4>How to use:</h4>
              <ol>
                <li>Generate a JWT token above</li>
                <li>Click &quot;Open Swagger UI&quot;</li>
                <li>Click the &quot;Authorize&quot; button</li>
                <li>Paste the token in the format: <code>Bearer &lt;token&gt;</code></li>
                <li>Test any endpoint</li>
              </ol>
            </div>
          </div>

          {/* Environment Info */}
          <div className="dev-tool-card">
            <h2>ℹ️ Environment Information</h2>

            <div className="env-details">
            </div>
          </div>
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default DevTools;
